/**
 * SpellData.ts - Complete FF1 spell definitions for frontend
 * All 64 spells with accurate stats, costs, and targeting
 */

// ============================================================================
// TYPES & ENUMS
// ============================================================================

export enum SpellType {
  WHITE = 'WHITE',
  BLACK = 'BLACK',
}

export enum Element {
  None = 0,
  Fire = 1,
  Ice = 2,
  Lightning = 3,
  Earth = 4,
  Wind = 5,
  Water = 6,
  Holy = 7,
  Dark = 8,
  Status = 9,
}

export enum SpellTarget {
  Self = 0,
  SingleAlly = 1,
  AllAllies = 2,
  SingleEnemy = 3,
  AllEnemies = 4,
}

export enum StatusEffect {
  None = 0,
  Dead = 1,
  Stone = 2,
  Poison = 3,
  Blind = 4,
  Paralysis = 5,
  Silence = 6,
  Sleep = 7,
  Confuse = 8,
}

export enum CharacterClass {
  Fighter = 0,
  Thief = 1,
  Monk = 2,
  RedMage = 3,
  WhiteMage = 4,
  BlackMage = 5,
}

// ============================================================================
// SPELL DEFINITION INTERFACE
// ============================================================================

export interface SpellDefinition {
  id: number;
  name: string;
  type: SpellType;
  level: number; // 1-8
  power: number; // Base power for damage/healing
  element: Element;
  target: SpellTarget;
  accuracy: number; // 0-100
  statusEffect: StatusEffect;
  statusChance: number; // 0-100
  isHealing: boolean;
  description: string;
  chargesPerLevel: number; // How many charges you get per spell level
  canBeLearned: CharacterClass[]; // Which classes can learn this spell
}

// ============================================================================
// CLASS RESTRICTIONS
// ============================================================================

const WHITE_MAGES = [CharacterClass.RedMage, CharacterClass.WhiteMage];
const BLACK_MAGES = [CharacterClass.RedMage, CharacterClass.BlackMage];
const WHITE_ONLY = [CharacterClass.WhiteMage];
const BLACK_ONLY = [CharacterClass.BlackMage];

// ============================================================================
// WHITE MAGIC SPELLS (IDs 1-32)
// ============================================================================

const WHITE_MAGIC: SpellDefinition[] = [
  // ===== LEVEL 1 WHITE MAGIC =====
  {
    id: 1,
    name: 'CURE',
    type: SpellType.WHITE,
    level: 1,
    power: 20,
    element: Element.Holy,
    target: SpellTarget.SingleAlly,
    accuracy: 100,
    statusEffect: StatusEffect.None,
    statusChance: 0,
    isHealing: true,
    description: 'Restore ~20 HP to one ally',
    chargesPerLevel: 4,
    canBeLearned: WHITE_MAGES,
  },
  {
    id: 2,
    name: 'FOG',
    type: SpellType.WHITE,
    level: 1,
    power: 0,
    element: Element.Status,
    target: SpellTarget.AllAllies,
    accuracy: 100,
    statusEffect: StatusEffect.None,
    statusChance: 0,
    isHealing: false,
    description: 'Increase party evasion',
    chargesPerLevel: 4,
    canBeLearned: WHITE_MAGES,
  },
  {
    id: 3,
    name: 'RUSE',
    type: SpellType.WHITE,
    level: 1,
    power: 0,
    element: Element.Status,
    target: SpellTarget.SingleAlly,
    accuracy: 100,
    statusEffect: StatusEffect.None,
    statusChance: 0,
    isHealing: false,
    description: 'Increase ally defense',
    chargesPerLevel: 4,
    canBeLearned: WHITE_MAGES,
  },
  {
    id: 4,
    name: 'HARM',
    type: SpellType.WHITE,
    level: 1,
    power: 25,
    element: Element.Holy,
    target: SpellTarget.SingleEnemy,
    accuracy: 80,
    statusEffect: StatusEffect.None,
    statusChance: 0,
    isHealing: false,
    description: 'Holy damage vs undead',
    chargesPerLevel: 4,
    canBeLearned: WHITE_MAGES,
  },

  // ===== LEVEL 2 WHITE MAGIC =====
  {
    id: 5,
    name: 'LAMP',
    type: SpellType.WHITE,
    level: 2,
    power: 0,
    element: Element.Holy,
    target: SpellTarget.Self,
    accuracy: 100,
    statusEffect: StatusEffect.None,
    statusChance: 0,
    isHealing: false,
    description: 'Light up dark dungeons',
    chargesPerLevel: 3,
    canBeLearned: WHITE_MAGES,
  },
  {
    id: 6,
    name: 'MUTE',
    type: SpellType.WHITE,
    level: 2,
    power: 0,
    element: Element.Status,
    target: SpellTarget.SingleEnemy,
    accuracy: 60,
    statusEffect: StatusEffect.Silence,
    statusChance: 60,
    isHealing: false,
    description: 'Silence one enemy',
    chargesPerLevel: 3,
    canBeLearned: WHITE_MAGES,
  },
  {
    id: 7,
    name: 'ALIT',
    type: SpellType.WHITE,
    level: 2,
    power: 0,
    element: Element.Lightning,
    target: SpellTarget.AllAllies,
    accuracy: 100,
    statusEffect: StatusEffect.None,
    statusChance: 0,
    isHealing: false,
    description: 'Resist lightning damage',
    chargesPerLevel: 3,
    canBeLearned: WHITE_MAGES,
  },
  {
    id: 8,
    name: 'INVS',
    type: SpellType.WHITE,
    level: 2,
    power: 0,
    element: Element.Status,
    target: SpellTarget.Self,
    accuracy: 100,
    statusEffect: StatusEffect.None,
    statusChance: 0,
    isHealing: false,
    description: 'Avoid random encounters',
    chargesPerLevel: 3,
    canBeLearned: WHITE_MAGES,
  },

  // ===== LEVEL 3 WHITE MAGIC =====
  {
    id: 9,
    name: 'CUR2',
    type: SpellType.WHITE,
    level: 3,
    power: 40,
    element: Element.Holy,
    target: SpellTarget.SingleAlly,
    accuracy: 100,
    statusEffect: StatusEffect.None,
    statusChance: 0,
    isHealing: true,
    description: 'Restore ~40 HP to one ally',
    chargesPerLevel: 3,
    canBeLearned: WHITE_MAGES,
  },
  {
    id: 10,
    name: 'HRM2',
    type: SpellType.WHITE,
    level: 3,
    power: 45,
    element: Element.Holy,
    target: SpellTarget.SingleEnemy,
    accuracy: 85,
    statusEffect: StatusEffect.None,
    statusChance: 0,
    isHealing: false,
    description: 'More holy damage vs undead',
    chargesPerLevel: 3,
    canBeLearned: WHITE_MAGES,
  },
  {
    id: 11,
    name: 'AFIR',
    type: SpellType.WHITE,
    level: 3,
    power: 0,
    element: Element.Fire,
    target: SpellTarget.AllAllies,
    accuracy: 100,
    statusEffect: StatusEffect.None,
    statusChance: 0,
    isHealing: false,
    description: 'Resist fire damage',
    chargesPerLevel: 3,
    canBeLearned: WHITE_MAGES,
  },
  {
    id: 12,
    name: 'HEAL',
    type: SpellType.WHITE,
    level: 3,
    power: 0,
    element: Element.Status,
    target: SpellTarget.SingleAlly,
    accuracy: 100,
    statusEffect: StatusEffect.Poison,
    statusChance: 100,
    isHealing: true,
    description: 'Cure poison on one ally',
    chargesPerLevel: 3,
    canBeLearned: WHITE_MAGES,
  },

  // ===== LEVEL 4 WHITE MAGIC =====
  {
    id: 13,
    name: 'PURE',
    type: SpellType.WHITE,
    level: 4,
    power: 0,
    element: Element.Status,
    target: SpellTarget.AllAllies,
    accuracy: 100,
    statusEffect: StatusEffect.Poison,
    statusChance: 100,
    isHealing: true,
    description: 'Cure poison on all allies',
    chargesPerLevel: 2,
    canBeLearned: WHITE_MAGES,
  },
  {
    id: 14,
    name: 'FEAR',
    type: SpellType.WHITE,
    level: 4,
    power: 0,
    element: Element.Status,
    target: SpellTarget.SingleEnemy,
    accuracy: 50,
    statusEffect: StatusEffect.Paralysis,
    statusChance: 50,
    isHealing: false,
    description: 'Paralyze one enemy',
    chargesPerLevel: 2,
    canBeLearned: WHITE_MAGES,
  },
  {
    id: 15,
    name: 'AICE',
    type: SpellType.WHITE,
    level: 4,
    power: 0,
    element: Element.Ice,
    target: SpellTarget.AllAllies,
    accuracy: 100,
    statusEffect: StatusEffect.None,
    statusChance: 0,
    isHealing: false,
    description: 'Resist ice damage',
    chargesPerLevel: 2,
    canBeLearned: WHITE_MAGES,
  },
  {
    id: 16,
    name: 'AMUT',
    type: SpellType.WHITE,
    level: 4,
    power: 0,
    element: Element.Status,
    target: SpellTarget.AllAllies,
    accuracy: 100,
    statusEffect: StatusEffect.None,
    statusChance: 0,
    isHealing: false,
    description: 'Resist silence',
    chargesPerLevel: 2,
    canBeLearned: WHITE_MAGES,
  },

  // ===== LEVEL 5 WHITE MAGIC =====
  {
    id: 17,
    name: 'CUR3',
    type: SpellType.WHITE,
    level: 5,
    power: 60,
    element: Element.Holy,
    target: SpellTarget.SingleAlly,
    accuracy: 100,
    statusEffect: StatusEffect.None,
    statusChance: 0,
    isHealing: true,
    description: 'Restore ~60 HP to one ally',
    chargesPerLevel: 2,
    canBeLearned: WHITE_MAGES,
  },
  {
    id: 18,
    name: 'LIFE',
    type: SpellType.WHITE,
    level: 5,
    power: 25,
    element: Element.Holy,
    target: SpellTarget.SingleAlly,
    accuracy: 100,
    statusEffect: StatusEffect.Dead,
    statusChance: 100,
    isHealing: true,
    description: 'Revive fallen ally with 25 HP',
    chargesPerLevel: 2,
    canBeLearned: WHITE_MAGES,
  },
  {
    id: 19,
    name: 'HRM3',
    type: SpellType.WHITE,
    level: 5,
    power: 80,
    element: Element.Holy,
    target: SpellTarget.SingleEnemy,
    accuracy: 90,
    statusEffect: StatusEffect.None,
    statusChance: 0,
    isHealing: false,
    description: 'Strong holy damage vs undead',
    chargesPerLevel: 2,
    canBeLearned: WHITE_MAGES,
  },
  {
    id: 20,
    name: 'HEL2',
    type: SpellType.WHITE,
    level: 5,
    power: 30,
    element: Element.Holy,
    target: SpellTarget.AllAllies,
    accuracy: 100,
    statusEffect: StatusEffect.None,
    statusChance: 0,
    isHealing: true,
    description: 'Restore ~30 HP to all allies',
    chargesPerLevel: 2,
    canBeLearned: WHITE_MAGES,
  },

  // ===== LEVEL 6 WHITE MAGIC =====
  {
    id: 21,
    name: 'SOFT',
    type: SpellType.WHITE,
    level: 6,
    power: 0,
    element: Element.Status,
    target: SpellTarget.SingleAlly,
    accuracy: 100,
    statusEffect: StatusEffect.Stone,
    statusChance: 100,
    isHealing: true,
    description: 'Cure stone on one ally',
    chargesPerLevel: 1,
    canBeLearned: WHITE_MAGES,
  },
  {
    id: 22,
    name: 'EXIT',
    type: SpellType.WHITE,
    level: 6,
    power: 0,
    element: Element.Status,
    target: SpellTarget.Self,
    accuracy: 100,
    statusEffect: StatusEffect.None,
    statusChance: 0,
    isHealing: false,
    description: 'Escape from dungeon',
    chargesPerLevel: 1,
    canBeLearned: WHITE_MAGES,
  },
  {
    id: 23,
    name: 'FOG2',
    type: SpellType.WHITE,
    level: 6,
    power: 0,
    element: Element.Status,
    target: SpellTarget.AllAllies,
    accuracy: 100,
    statusEffect: StatusEffect.None,
    statusChance: 0,
    isHealing: false,
    description: 'Greatly increase evasion',
    chargesPerLevel: 1,
    canBeLearned: WHITE_MAGES,
  },
  {
    id: 24,
    name: 'INV2',
    type: SpellType.WHITE,
    level: 6,
    power: 0,
    element: Element.Status,
    target: SpellTarget.Self,
    accuracy: 100,
    statusEffect: StatusEffect.None,
    statusChance: 0,
    isHealing: false,
    description: 'Enhanced invisibility',
    chargesPerLevel: 1,
    canBeLearned: WHITE_ONLY,
  },

  // ===== LEVEL 7 WHITE MAGIC =====
  {
    id: 25,
    name: 'CUR4',
    type: SpellType.WHITE,
    level: 7,
    power: 100,
    element: Element.Holy,
    target: SpellTarget.SingleAlly,
    accuracy: 100,
    statusEffect: StatusEffect.None,
    statusChance: 0,
    isHealing: true,
    description: 'Restore ~100 HP to one ally',
    chargesPerLevel: 1,
    canBeLearned: WHITE_ONLY,
  },
  {
    id: 26,
    name: 'HRM4',
    type: SpellType.WHITE,
    level: 7,
    power: 120,
    element: Element.Holy,
    target: SpellTarget.SingleEnemy,
    accuracy: 95,
    statusEffect: StatusEffect.None,
    statusChance: 0,
    isHealing: false,
    description: 'Devastating holy damage',
    chargesPerLevel: 1,
    canBeLearned: WHITE_ONLY,
  },
  {
    id: 27,
    name: 'ARUB',
    type: SpellType.WHITE,
    level: 7,
    power: 0,
    element: Element.Status,
    target: SpellTarget.AllAllies,
    accuracy: 100,
    statusEffect: StatusEffect.None,
    statusChance: 100,
    isHealing: true,
    description: 'Cure all status ailments',
    chargesPerLevel: 1,
    canBeLearned: WHITE_ONLY,
  },
  {
    id: 28,
    name: 'HEL3',
    type: SpellType.WHITE,
    level: 7,
    power: 60,
    element: Element.Holy,
    target: SpellTarget.AllAllies,
    accuracy: 100,
    statusEffect: StatusEffect.None,
    statusChance: 0,
    isHealing: true,
    description: 'Restore ~60 HP to all allies',
    chargesPerLevel: 1,
    canBeLearned: WHITE_ONLY,
  },

  // ===== LEVEL 8 WHITE MAGIC =====
  {
    id: 29,
    name: 'FADE',
    type: SpellType.WHITE,
    level: 8,
    power: 0,
    element: Element.Status,
    target: SpellTarget.Self,
    accuracy: 100,
    statusEffect: StatusEffect.None,
    statusChance: 0,
    isHealing: false,
    description: 'Teleport to dungeon entrance',
    chargesPerLevel: 1,
    canBeLearned: WHITE_ONLY,
  },
  {
    id: 30,
    name: 'WALL',
    type: SpellType.WHITE,
    level: 8,
    power: 0,
    element: Element.Status,
    target: SpellTarget.SingleAlly,
    accuracy: 100,
    statusEffect: StatusEffect.None,
    statusChance: 0,
    isHealing: false,
    description: 'Reflect magic attacks',
    chargesPerLevel: 1,
    canBeLearned: WHITE_ONLY,
  },
  {
    id: 31,
    name: 'XFER',
    type: SpellType.WHITE,
    level: 8,
    power: 50,
    element: Element.Holy,
    target: SpellTarget.SingleAlly,
    accuracy: 100,
    statusEffect: StatusEffect.None,
    statusChance: 0,
    isHealing: true,
    description: 'Transfer HP to another ally',
    chargesPerLevel: 1,
    canBeLearned: WHITE_ONLY,
  },
  {
    id: 32,
    name: 'LIF2',
    type: SpellType.WHITE,
    level: 8,
    power: 100,
    element: Element.Holy,
    target: SpellTarget.SingleAlly,
    accuracy: 100,
    statusEffect: StatusEffect.Dead,
    statusChance: 100,
    isHealing: true,
    description: 'Full revive with 100% HP',
    chargesPerLevel: 1,
    canBeLearned: WHITE_ONLY,
  },
];

// ============================================================================
// BLACK MAGIC SPELLS (IDs 33-64)
// ============================================================================

const BLACK_MAGIC: SpellDefinition[] = [
  // ===== LEVEL 1 BLACK MAGIC =====
  {
    id: 33,
    name: 'FIRE',
    type: SpellType.BLACK,
    level: 1,
    power: 15,
    element: Element.Fire,
    target: SpellTarget.SingleEnemy,
    accuracy: 85,
    statusEffect: StatusEffect.None,
    statusChance: 0,
    isHealing: false,
    description: 'Fire damage to one enemy',
    chargesPerLevel: 4,
    canBeLearned: BLACK_MAGES,
  },
  {
    id: 34,
    name: 'ICE',
    type: SpellType.BLACK,
    level: 1,
    power: 15,
    element: Element.Ice,
    target: SpellTarget.SingleEnemy,
    accuracy: 85,
    statusEffect: StatusEffect.None,
    statusChance: 0,
    isHealing: false,
    description: 'Ice damage to one enemy',
    chargesPerLevel: 4,
    canBeLearned: BLACK_MAGES,
  },
  {
    id: 35,
    name: 'LIT',
    type: SpellType.BLACK,
    level: 1,
    power: 15,
    element: Element.Lightning,
    target: SpellTarget.SingleEnemy,
    accuracy: 85,
    statusEffect: StatusEffect.None,
    statusChance: 0,
    isHealing: false,
    description: 'Lightning damage to one enemy',
    chargesPerLevel: 4,
    canBeLearned: BLACK_MAGES,
  },
  {
    id: 36,
    name: 'SLEEP',
    type: SpellType.BLACK,
    level: 1,
    power: 0,
    element: Element.Status,
    target: SpellTarget.SingleEnemy,
    accuracy: 70,
    statusEffect: StatusEffect.Sleep,
    statusChance: 70,
    isHealing: false,
    description: 'Put one enemy to sleep',
    chargesPerLevel: 4,
    canBeLearned: BLACK_MAGES,
  },

  // ===== LEVEL 2 BLACK MAGIC =====
  {
    id: 37,
    name: 'ICE2',
    type: SpellType.BLACK,
    level: 2,
    power: 30,
    element: Element.Ice,
    target: SpellTarget.SingleEnemy,
    accuracy: 88,
    statusEffect: StatusEffect.None,
    statusChance: 0,
    isHealing: false,
    description: 'Stronger ice damage',
    chargesPerLevel: 3,
    canBeLearned: BLACK_MAGES,
  },
  {
    id: 38,
    name: 'DARK',
    type: SpellType.BLACK,
    level: 2,
    power: 0,
    element: Element.Dark,
    target: SpellTarget.SingleEnemy,
    accuracy: 65,
    statusEffect: StatusEffect.Blind,
    statusChance: 65,
    isHealing: false,
    description: 'Blind one enemy',
    chargesPerLevel: 3,
    canBeLearned: BLACK_MAGES,
  },
  {
    id: 39,
    name: 'TMPR',
    type: SpellType.BLACK,
    level: 2,
    power: 0,
    element: Element.Status,
    target: SpellTarget.SingleAlly,
    accuracy: 100,
    statusEffect: StatusEffect.None,
    statusChance: 0,
    isHealing: false,
    description: 'Increase ally attack power',
    chargesPerLevel: 3,
    canBeLearned: BLACK_MAGES,
  },
  {
    id: 40,
    name: 'SLOW',
    type: SpellType.BLACK,
    level: 2,
    power: 0,
    element: Element.Status,
    target: SpellTarget.SingleEnemy,
    accuracy: 60,
    statusEffect: StatusEffect.None,
    statusChance: 60,
    isHealing: false,
    description: 'Reduce enemy speed',
    chargesPerLevel: 3,
    canBeLearned: BLACK_MAGES,
  },

  // ===== LEVEL 3 BLACK MAGIC =====
  {
    id: 41,
    name: 'FIR2',
    type: SpellType.BLACK,
    level: 3,
    power: 40,
    element: Element.Fire,
    target: SpellTarget.SingleEnemy,
    accuracy: 90,
    statusEffect: StatusEffect.None,
    statusChance: 0,
    isHealing: false,
    description: 'Stronger fire damage',
    chargesPerLevel: 3,
    canBeLearned: BLACK_MAGES,
  },
  {
    id: 42,
    name: 'HOLD',
    type: SpellType.BLACK,
    level: 3,
    power: 0,
    element: Element.Status,
    target: SpellTarget.SingleEnemy,
    accuracy: 55,
    statusEffect: StatusEffect.Paralysis,
    statusChance: 55,
    isHealing: false,
    description: 'Paralyze one enemy',
    chargesPerLevel: 3,
    canBeLearned: BLACK_MAGES,
  },
  {
    id: 43,
    name: 'LIT2',
    type: SpellType.BLACK,
    level: 3,
    power: 40,
    element: Element.Lightning,
    target: SpellTarget.SingleEnemy,
    accuracy: 90,
    statusEffect: StatusEffect.None,
    statusChance: 0,
    isHealing: false,
    description: 'Stronger lightning damage',
    chargesPerLevel: 3,
    canBeLearned: BLACK_MAGES,
  },
  {
    id: 44,
    name: 'LOK2',
    type: SpellType.BLACK,
    level: 3,
    power: 0,
    element: Element.Status,
    target: SpellTarget.SingleEnemy,
    accuracy: 60,
    statusEffect: StatusEffect.Silence,
    statusChance: 60,
    isHealing: false,
    description: 'Silence one enemy',
    chargesPerLevel: 3,
    canBeLearned: BLACK_MAGES,
  },

  // ===== LEVEL 4 BLACK MAGIC =====
  {
    id: 45,
    name: 'SLP2',
    type: SpellType.BLACK,
    level: 4,
    power: 0,
    element: Element.Status,
    target: SpellTarget.AllEnemies,
    accuracy: 50,
    statusEffect: StatusEffect.Sleep,
    statusChance: 50,
    isHealing: false,
    description: 'Put all enemies to sleep',
    chargesPerLevel: 2,
    canBeLearned: BLACK_MAGES,
  },
  {
    id: 46,
    name: 'FAST',
    type: SpellType.BLACK,
    level: 4,
    power: 0,
    element: Element.Status,
    target: SpellTarget.SingleAlly,
    accuracy: 100,
    statusEffect: StatusEffect.None,
    statusChance: 0,
    isHealing: false,
    description: 'Increase ally speed',
    chargesPerLevel: 2,
    canBeLearned: BLACK_MAGES,
  },
  {
    id: 47,
    name: 'CONF',
    type: SpellType.BLACK,
    level: 4,
    power: 0,
    element: Element.Status,
    target: SpellTarget.SingleEnemy,
    accuracy: 55,
    statusEffect: StatusEffect.Confuse,
    statusChance: 55,
    isHealing: false,
    description: 'Confuse one enemy',
    chargesPerLevel: 2,
    canBeLearned: BLACK_MAGES,
  },
  {
    id: 48,
    name: 'ICE3',
    type: SpellType.BLACK,
    level: 4,
    power: 60,
    element: Element.Ice,
    target: SpellTarget.SingleEnemy,
    accuracy: 92,
    statusEffect: StatusEffect.None,
    statusChance: 0,
    isHealing: false,
    description: 'Strong ice damage',
    chargesPerLevel: 2,
    canBeLearned: BLACK_MAGES,
  },

  // ===== LEVEL 5 BLACK MAGIC =====
  {
    id: 49,
    name: 'FIR3',
    type: SpellType.BLACK,
    level: 5,
    power: 75,
    element: Element.Fire,
    target: SpellTarget.SingleEnemy,
    accuracy: 95,
    statusEffect: StatusEffect.None,
    statusChance: 0,
    isHealing: false,
    description: 'Strong fire damage',
    chargesPerLevel: 2,
    canBeLearned: BLACK_MAGES,
  },
  {
    id: 50,
    name: 'BANE',
    type: SpellType.BLACK,
    level: 5,
    power: 200,
    element: Element.Dark,
    target: SpellTarget.SingleEnemy,
    accuracy: 30,
    statusEffect: StatusEffect.Dead,
    statusChance: 30,
    isHealing: false,
    description: 'Instant death spell',
    chargesPerLevel: 2,
    canBeLearned: BLACK_MAGES,
  },
  {
    id: 51,
    name: 'WARP',
    type: SpellType.BLACK,
    level: 5,
    power: 0,
    element: Element.Status,
    target: SpellTarget.Self,
    accuracy: 100,
    statusEffect: StatusEffect.None,
    statusChance: 0,
    isHealing: false,
    description: 'Return to dungeon entrance',
    chargesPerLevel: 2,
    canBeLearned: BLACK_MAGES,
  },
  {
    id: 52,
    name: 'SLO2',
    type: SpellType.BLACK,
    level: 5,
    power: 0,
    element: Element.Status,
    target: SpellTarget.AllEnemies,
    accuracy: 50,
    statusEffect: StatusEffect.None,
    statusChance: 50,
    isHealing: false,
    description: 'Slow all enemies',
    chargesPerLevel: 2,
    canBeLearned: BLACK_MAGES,
  },

  // ===== LEVEL 6 BLACK MAGIC =====
  {
    id: 53,
    name: 'LIT3',
    type: SpellType.BLACK,
    level: 6,
    power: 75,
    element: Element.Lightning,
    target: SpellTarget.SingleEnemy,
    accuracy: 95,
    statusEffect: StatusEffect.None,
    statusChance: 0,
    isHealing: false,
    description: 'Strong lightning damage',
    chargesPerLevel: 1,
    canBeLearned: BLACK_MAGES,
  },
  {
    id: 54,
    name: 'RUB',
    type: SpellType.BLACK,
    level: 6,
    power: 200,
    element: Element.Dark,
    target: SpellTarget.AllEnemies,
    accuracy: 20,
    statusEffect: StatusEffect.Dead,
    statusChance: 20,
    isHealing: false,
    description: 'Instant death to all enemies',
    chargesPerLevel: 1,
    canBeLearned: BLACK_MAGES,
  },
  {
    id: 55,
    name: 'QAKE',
    type: SpellType.BLACK,
    level: 6,
    power: 60,
    element: Element.Earth,
    target: SpellTarget.AllEnemies,
    accuracy: 85,
    statusEffect: StatusEffect.None,
    statusChance: 0,
    isHealing: false,
    description: 'Earth damage to all enemies',
    chargesPerLevel: 1,
    canBeLearned: BLACK_MAGES,
  },
  {
    id: 56,
    name: 'STUN',
    type: SpellType.BLACK,
    level: 6,
    power: 0,
    element: Element.Status,
    target: SpellTarget.AllEnemies,
    accuracy: 40,
    statusEffect: StatusEffect.Paralysis,
    statusChance: 40,
    isHealing: false,
    description: 'Paralyze all enemies',
    chargesPerLevel: 1,
    canBeLearned: BLACK_ONLY,
  },

  // ===== LEVEL 7 BLACK MAGIC =====
  {
    id: 57,
    name: 'BRAK',
    type: SpellType.BLACK,
    level: 7,
    power: 0,
    element: Element.Status,
    target: SpellTarget.SingleEnemy,
    accuracy: 100,
    statusEffect: StatusEffect.None,
    statusChance: 0,
    isHealing: false,
    description: 'Reduce enemy defense',
    chargesPerLevel: 1,
    canBeLearned: BLACK_ONLY,
  },
  {
    id: 58,
    name: 'SABR',
    type: SpellType.BLACK,
    level: 7,
    power: 0,
    element: Element.Status,
    target: SpellTarget.SingleAlly,
    accuracy: 100,
    statusEffect: StatusEffect.None,
    statusChance: 0,
    isHealing: false,
    description: 'Greatly increase attack',
    chargesPerLevel: 1,
    canBeLearned: BLACK_ONLY,
  },
  {
    id: 59,
    name: 'BLND',
    type: SpellType.BLACK,
    level: 7,
    power: 0,
    element: Element.Dark,
    target: SpellTarget.AllEnemies,
    accuracy: 50,
    statusEffect: StatusEffect.Blind,
    statusChance: 50,
    isHealing: false,
    description: 'Blind all enemies',
    chargesPerLevel: 1,
    canBeLearned: BLACK_ONLY,
  },
  {
    id: 60,
    name: 'ICE4',
    type: SpellType.BLACK,
    level: 7,
    power: 100,
    element: Element.Ice,
    target: SpellTarget.AllEnemies,
    accuracy: 90,
    statusEffect: StatusEffect.None,
    statusChance: 0,
    isHealing: false,
    description: 'Devastating ice to all',
    chargesPerLevel: 1,
    canBeLearned: BLACK_ONLY,
  },

  // ===== LEVEL 8 BLACK MAGIC =====
  {
    id: 61,
    name: 'NUKE',
    type: SpellType.BLACK,
    level: 8,
    power: 150,
    element: Element.None,
    target: SpellTarget.AllEnemies,
    accuracy: 95,
    statusEffect: StatusEffect.None,
    statusChance: 0,
    isHealing: false,
    description: 'Massive non-elemental damage',
    chargesPerLevel: 1,
    canBeLearned: BLACK_ONLY,
  },
  {
    id: 62,
    name: 'STOP',
    type: SpellType.BLACK,
    level: 8,
    power: 0,
    element: Element.Status,
    target: SpellTarget.AllEnemies,
    accuracy: 40,
    statusEffect: StatusEffect.Paralysis,
    statusChance: 40,
    isHealing: false,
    description: 'Stop all enemies in time',
    chargesPerLevel: 1,
    canBeLearned: BLACK_ONLY,
  },
  {
    id: 63,
    name: 'ZAP!',
    type: SpellType.BLACK,
    level: 8,
    power: 120,
    element: Element.Lightning,
    target: SpellTarget.AllEnemies,
    accuracy: 95,
    statusEffect: StatusEffect.None,
    statusChance: 0,
    isHealing: false,
    description: 'Devastating lightning to all',
    chargesPerLevel: 1,
    canBeLearned: BLACK_ONLY,
  },
  {
    id: 64,
    name: 'XXXX',
    type: SpellType.BLACK,
    level: 8,
    power: 250,
    element: Element.Dark,
    target: SpellTarget.AllEnemies,
    accuracy: 25,
    statusEffect: StatusEffect.Dead,
    statusChance: 25,
    isHealing: false,
    description: 'Ultimate instant death',
    chargesPerLevel: 1,
    canBeLearned: BLACK_ONLY,
  },
];

// ============================================================================
// SPELL DATABASE
// ============================================================================

export const ALL_SPELLS: Map<number, SpellDefinition> = new Map([
  ...WHITE_MAGIC.map((spell) => [spell.id, spell] as [number, SpellDefinition]),
  ...BLACK_MAGIC.map((spell) => [spell.id, spell] as [number, SpellDefinition]),
]);

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get spell by ID
 */
export function getSpell(spellId: number): SpellDefinition | undefined {
  return ALL_SPELLS.get(spellId);
}

/**
 * Get spells by level
 */
export function getSpellsByLevel(level: number, type?: SpellType): SpellDefinition[] {
  const spells = Array.from(ALL_SPELLS.values()).filter((spell) => spell.level === level);

  if (type) {
    return spells.filter((spell) => spell.type === type);
  }

  return spells;
}

/**
 * Get spells a character class can learn
 */
export function getSpellsForClass(characterClass: CharacterClass): SpellDefinition[] {
  return Array.from(ALL_SPELLS.values()).filter((spell) =>
    spell.canBeLearned.includes(characterClass)
  );
}

/**
 * Check if a character class can learn a spell
 */
export function canClassLearnSpell(characterClass: CharacterClass, spellId: number): boolean {
  const spell = getSpell(spellId);
  return spell ? spell.canBeLearned.includes(characterClass) : false;
}

/**
 * Get element name as string
 */
export function getElementName(element: Element): string {
  return Element[element] || 'None';
}

/**
 * Get spell target description
 */
export function getTargetDescription(target: SpellTarget): string {
  switch (target) {
    case SpellTarget.Self:
      return 'Self';
    case SpellTarget.SingleAlly:
      return 'One Ally';
    case SpellTarget.AllAllies:
      return 'All Allies';
    case SpellTarget.SingleEnemy:
      return 'One Enemy';
    case SpellTarget.AllEnemies:
      return 'All Enemies';
    default:
      return 'Unknown';
  }
}

/**
 * Get spell color based on element (for visual effects)
 */
export function getSpellColor(element: Element): string {
  switch (element) {
    case Element.Fire:
      return '#ff4400';
    case Element.Ice:
      return '#44aaff';
    case Element.Lightning:
      return '#ffff00';
    case Element.Earth:
      return '#aa6622';
    case Element.Wind:
      return '#88ff88';
    case Element.Water:
      return '#0088ff';
    case Element.Holy:
      return '#ffffff';
    case Element.Dark:
      return '#440088';
    case Element.Status:
      return '#ff00ff';
    default:
      return '#ffffff';
  }
}
